/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { useFormatDateTime, Button, DividerNew } from 'sarsaparilla';

import { UIcon } from 'shared-ui/uicon';

import { NumberField } from './NumberField';
import type {
    DatePickerState,
    DatePickerVariant,
    MonthsOption,
} from './DatePicker.types';
import { getFirstSelectableMonth, SELECTABLE_MONTHS_COUNT } from './DatePicker.utils';

import styles from './FlexiblePanel.module.scss';

const MAX_SELECTABLE_MONTHS = 3;

interface FlexiblePanelProps {
    state: DatePickerState;
    setState: React.Dispatch<React.SetStateAction<DatePickerState>>;
    /** In the drawer, only the month grid scrolls; the nights field and heading stay pinned. */
    variant?: DatePickerVariant;
    /** GA event category. Omit to send no click events. */
    clickTagCategory?: string;
}

export function FlexiblePanel({
    state,
    setState,
    variant = 'popover',
    clickTagCategory,
}: FlexiblePanelProps) {
    const monthsOptions = useMonthsOptions();

    const selectedOptionsText = React.useMemo(() => {
        const selectedMonths = monthsOptions
            .filter((item) => state.availMonths?.includes(item.value))
            .map((item) => item.month);

        if (selectedMonths.length === 1) {
            return selectedMonths[0];
        }

        return `${selectedMonths.slice(0, -1).join(', ')} or ${selectedMonths[selectedMonths.length - 1]}`;
    }, [monthsOptions, state.availMonths]);

    const handleChecked = (value: string) => {
        const alreadyChecked = state.availMonths?.find((item) => item === value);

        if (alreadyChecked) {
            // Nights are kept so reselecting a month restores them; the commit
            // drops nights while no month is selected.
            setState((prev) => {
                const availMonths =
                    prev.availMonths?.filter((item) => item !== value) ?? [];

                return {
                    ...prev,
                    availMonths: availMonths.length > 0 ? availMonths : null,
                };
            });
        } else {
            setState((prev) => ({
                ...prev,
                availNights: prev.availNights ?? 1,
                availMonths: [...(prev.availMonths ?? []), value],
            }));
        }
    };

    const numSelectedMonths = state.availMonths?.length ?? 0;
    const isDisabled = numSelectedMonths >= MAX_SELECTABLE_MONTHS;

    return (
        <div className={cx(styles.flexTab, { [styles.isDrawer]: variant === 'drawer' })}>
            <div className={styles.pinnedHeader}>
                <div className={styles.numNightsContainer}>
                    <div className="text-lg font-weight-semibold">Number of nights</div>

                    <NumberField
                        aria-label="Number of Nights"
                        value={state.availNights ?? 1}
                        min={1}
                        max={14}
                        step={1}
                        smallStep={1}
                        largeStep={5}
                        // An emptied input reports null; fall back to the field's minimum.
                        onValueChange={(value) =>
                            setState((prev) => ({ ...prev, availNights: value ?? 1 }))
                        }
                    />
                </div>

                <DividerNew className="my-2" />

                <div
                    className={cx(
                        styles.numSelectedMonthsText,
                        'text-lg my-2 font-weight-semibold',
                        {
                            'color-text-01': numSelectedMonths > 0,
                            'color-text-03': numSelectedMonths === 0,
                        }
                    )}
                >
                    {numSelectedMonths > 0
                        ? `Go in ${selectedOptionsText}`
                        : `Select up to ${MAX_SELECTABLE_MONTHS} months ...`}
                </div>
            </div>

            <div className={styles.monthOptionsScrollArea}>
                <div className={styles.monthOptionsGrid}>
                    {monthsOptions.map((item) => {
                        const isChecked = state.availMonths?.includes(item?.value);
                        const GATagAction = isChecked ? 'Deselected' : 'Selected';

                        return (
                            <Button
                                isUnstyled
                                key={item.value}
                                role="checkbox"
                                onClick={() => handleChecked(item.value)}
                                aria-label={`${item.month}, ${item.year}`}
                                aria-checked={isChecked}
                                clickTagCategory={clickTagCategory}
                                clickTagAction={`${GATagAction} - ${item.month}, ${item.year}`}
                                clickTagLabel="Flexible Booking"
                                isDisabled={(isDisabled && !isChecked) || false}
                                className={cx(styles.monthOptionButton, {
                                    [styles.selected]: isChecked,
                                    [styles.isDisabled]: isDisabled && !isChecked,
                                })}
                            >
                                <UIcon
                                    name={isChecked ? 'calendar-check' : 'calendar'}
                                    absoluteStrokeWidth
                                    size={24}
                                />

                                <span className={styles.monthOptionText}>
                                    <span className="text-md font-weight-semibold">
                                        {item.month}
                                    </span>
                                    <span className={styles.monthOptionYear}>
                                        {item.year}
                                    </span>
                                </span>
                            </Button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export function useMonthsOptions(): MonthsOption[] {
    const formatDateTime = useFormatDateTime();

    return React.useMemo(() => {
        const firstDayOfThisMonth = getFirstSelectableMonth();

        const monthsOptions: MonthsOption[] = [
            ...new Array(SELECTABLE_MONTHS_COUNT).keys(),
        ].map((item) => {
            const date = firstDayOfThisMonth.add({ months: item });
            const value = date.toString();
            const [year] = value.split('-');

            return {
                month: formatDateTime(date, { month: 'long' }),
                monthShort: formatDateTime(date, { month: 'short' }),
                year,
                value,
            };
        });

        return monthsOptions;
    }, [formatDateTime]);
}
